import { FeatureFlag, Tenant } from '../../Interfaces/Types'

export const sortByLabel = (a: FeatureFlag, b: FeatureFlag) => {
  return a.label.localeCompare(b.label)
}

// Status colors come from the --tma-status-* tokens so the accessibility
// color-vision themes can substitute distinguishable hues (see index.css).
// NOTE: these are var() strings — derive alpha tints with CSS color-mix(),
// never by appending a hex alpha suffix.
const statusColor = (item: Tenant) => {
  switch (item.deployStatus) {
    case 'initializing':
    case 'initialized':
      return 'var(--tma-status-info)'
    case 'updating':
      return 'var(--tma-status-progress)'
    case 'feWaiting':
      return 'var(--tma-status-wait)'
    case 'notCreated':
    case 'initializingFailed':
    case 'updatingFailed':
    case 'pendingDelete':
    case 'deleting':
      return 'var(--tma-status-failed)'
    case 'finished':
      return 'var(--tma-status-ok)'
    case 'unknown':
    default:
      return 'var(--tma-status-unknown)'
  }
}

const activeStatusColor = (item: Tenant) => {
  switch (item.deployStatus) {
    case 'initializing':
    case 'initialized':
      return 'var(--tma-status-info-strong)'
    case 'updating':
      return 'var(--tma-status-progress-strong)'
    case 'feWaiting':
      return 'var(--tma-status-wait-strong)'
    case 'notCreated':
    case 'initializingFailed':
    case 'updatingFailed':
    case 'pendingDelete':
    case 'deleting':
      return 'var(--tma-status-failed-strong)'
    case 'finished':
      return 'var(--tma-status-ok-strong)'
    case 'unknown':
    default:
      return 'var(--tma-status-unknown-strong)'
  }
}

export const itemColor = (tenant: Tenant | undefined, isActive: boolean) => {
  if (tenant !== undefined) {
    if (isActive) {
      return activeStatusColor(tenant)
    }
    return statusColor(tenant)
  } else if (isActive) {
    return '#010cd7'
  }
  return 'var(--tma-accent)'
}

export const environmentLetters = (item: Tenant) => {
  switch (item.environment) {
    case 'alpha':
      return 'A'
    case 'stable-alpha':
      return 'STA'
    case 'beta':
      return 'B'
    case 'production':
      return 'P'
  }
}

export const itemBackgroundColor = (isActive: boolean) => {
  if (isActive) return '#dededf'
  return '#f2f2f4'
}

export const downloadFile = (fileName: string, contents: string, mimeType: string): void => {
  const url = URL.createObjectURL(new Blob([contents], { type: mimeType }))
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Reads the message an API error carried: a plain-text body, or the message field of a
// JSON body such as the maintenance guard's { maintenance, message }.
export const apiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.data
  if (typeof data === 'string' && data) return data
  if (data && typeof data.message === 'string' && data.message) return data.message
  return fallback
}

// Client-secret expiry is local in the browser and UTC on the server: the API stores the
// instant the operator's local expiry day starts and never decides what "today" is, so the
// warning window and the search bound live here only.
export const CLIENT_SECRET_WARNING_DAYS = 14
export type ClientSecretExpiryState = 'none' | 'ok' | 'expiring' | 'expired'

const startOfLocalDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

const addLocalDays = (date: Date, days: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)

const localIsoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

// Calendar days between two local midnights. Rounding absorbs the hour a DST switch adds
// or removes, which plain millisecond division would turn into an off-by-one.
const localDaysBetween = (from: Date, to: Date) => Math.round((to.getTime() - from.getTime()) / 86400000)

export const startOfLocalDayUtcIso = (date: Date): string => startOfLocalDay(date).toISOString()

export const msUntilNextLocalMidnight = (now: Date = new Date()): number => addLocalDays(now, 1).getTime() - now.getTime()

// "…:00.000Z" as sent and "…:00Z" as returned by the API are the same instant.
export const sameInstant = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !a || !b ? !a && !b : new Date(a).getTime() === new Date(b).getTime()

export const clientSecretExpiryStatus = (
  expiresOnUtc: string | null | undefined,
  now: Date = new Date()
): { state: ClientSecretExpiryState; days: number | null; localDate: string | null } => {
  if (!expiresOnUtc) return { state: 'none', days: null, localDate: null }
  const expiresOn = new Date(expiresOnUtc)
  if (isNaN(expiresOn.getTime())) return { state: 'none', days: null, localDate: null }

  const days = localDaysBetween(startOfLocalDay(now), startOfLocalDay(expiresOn))
  const state: ClientSecretExpiryState = days < 0 ? 'expired' : days <= CLIENT_SECRET_WARNING_DAYS ? 'expiring' : 'ok'
  return { state, days, localDate: localIsoDate(expiresOn) }
}

// Exclusive upper bound for the "expiring" search: the start of the local day after the
// warning window, so the result matches exactly the tenants clientSecretExpiryStatus flags.
export const clientSecretExpiringBoundUtcIso = (now: Date = new Date()): string =>
  addLocalDays(now, CLIENT_SECRET_WARNING_DAYS + 1).toISOString()

export const clientSecretExpiryText = (status: ReturnType<typeof clientSecretExpiryStatus>): string | null => {
  switch (status.state) {
    case 'expired':
      return `Client secret expired on ${status.localDate}`
    case 'expiring':
      return status.days === 0
        ? `Client secret expires today (${status.localDate})`
        : `Client secret expires in ${status.days} day${status.days === 1 ? '' : 's'} (${status.localDate})`
    case 'ok':
      return `Client secret expires on ${status.localDate}`
    default:
      return null
  }
}

// Renders an ISO timestamp (e.g. "2023-12-18T07:38:14.4729379Z") as a readable
// local date/time like "18 Dec 2023, 08:38". Falls back to the raw value.
export const formatDateTime = (value?: string | null) => {
  if (!value) return ''
  const date = new Date(value)
  if (isNaN(date.getTime())) return value
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
